/**
 * Sign-in page (11-Authentication/Drafts/01-welcome-light-v5.png).
 * Provider buttons start real OAuth flows when the Auth server has that provider
 * enabled; otherwise they explain it is not set up here. Email continues to a
 * password step against Supabase Auth.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { CreditCard, FileText, Truck } from '../ui/glyphs.tsx';
import { supabase, configured, enabledProviders, type OAuthProvider } from '../lib/session.ts';
import { useI18n, type MessageKey } from '../lib/i18n.tsx';
import type { Prefs } from '../lib/prefs.ts';
import { Banner, Button } from '../ui/ui.tsx';
import { AppleMark, GoogleMark, MicrosoftMark, Wordmark } from '../ui/icons.tsx';
import { HelpLink, LegalLinks, PrefsCluster } from './PublicChrome.tsx';

type Problem = 'failed' | 'offline' | 'signupFailed' | { provider: string } | null;

const PROVIDERS: { id: OAuthProvider; label: MessageKey; name: string; icon: ReactElement }[] = [
  { id: 'apple', label: 'auth.withApple', name: 'Apple', icon: <AppleMark /> },
  { id: 'google', label: 'auth.withGoogle', name: 'Google', icon: <GoogleMark /> },
  { id: 'azure', label: 'auth.withMicrosoft', name: 'Microsoft', icon: <MicrosoftMark /> },
];

export function SignIn({ prefs, onPrefs }: { prefs: Prefs; onPrefs: (p: Prefs) => void }) {
  const { t } = useI18n();
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem>(null);
  const [providers, setProviders] = useState<Set<OAuthProvider> | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => { enabledProviders().then(setProviders).catch(() => setProviders(new Set())); }, []); // ignored: email sign-in stays offered and reports its own failure
  useEffect(() => { if (step === 'password') passwordRef.current?.focus(); }, [step]);

  async function oauth(provider: OAuthProvider, name: string) {
    setProblem(null);
    if (!providers?.has(provider)) { setProblem({ provider: name }); return; }
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: location.origin } });
    if (error) setProblem({ provider: name });
  }

  function continueEmail(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setProblem(null);
    setStep('password');
  }

  async function signIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) setProblem(error.status && error.status < 500 ? 'failed' : 'offline');
    } catch {
      setProblem('offline');
    } finally {
      setPassword('');
      setBusy(false);
    }
  }

  async function signUp() {
    if (!password) { passwordRef.current?.focus(); return; }
    setBusy(true);
    setProblem(null);
    try {
      const { error } = await supabase.auth.signUp({ email: email.trim(), password });
      if (error) setProblem(error.status && error.status < 500 ? 'signupFailed' : 'offline');
    } catch {
      setProblem('offline');
    } finally {
      setPassword('');
      setBusy(false);
    }
  }

  const message = problem === null ? null
    : typeof problem === 'object' ? t('auth.providerUnavailable', { provider: problem.provider })
    : t(problem === 'failed' ? 'auth.failed' : problem === 'signupFailed' ? 'auth.signupFailed' : 'auth.offline');

  return (
    <div className="public">
      <header className="public-head">
        <Wordmark size="lg" />
        <HelpLink />
      </header>
      <main className="signin">
        <section className="signin-pitch" aria-label={t('auth.headline')}>
          <h1>{t('auth.headline').split(', ').map((part, i, all) => <span key={part}>{part}{i < all.length - 1 ? ',' : ''}{i < all.length - 1 && <br />}</span>)}</h1>
          <p className="lede">{t('auth.lede')}</p>
          <ul>
            <li><Truck size={38} strokeWidth={1.5} />{t('auth.feature.dispatch')}</li>
            <li><FileText size={38} strokeWidth={1.5} />{t('auth.feature.documents')}</li>
            <li><CreditCard size={38} strokeWidth={1.5} />{t('auth.feature.invoices')}</li>
          </ul>
        </section>
        <section className="signin-form" aria-labelledby="auth-title">
          <h2 id="auth-title">{t('auth.title')}</h2>
          <p className="sub">{t('auth.subtitle')}</p>
          {!configured && <Banner tone="attention">{t('auth.notConfigured')}</Banner>}
          {step === 'email' ? (
            <>
              <div className="provider-list">
                {PROVIDERS.map(p => (
                  <button key={p.id} type="button" className="provider-btn" disabled={!configured} onClick={() => void oauth(p.id, p.name)}>{p.icon}{t(p.label)}</button>
                ))}
              </div>
              <div className="or-rule">{t('auth.or')}</div>
              {message && <Banner tone="attention">{message}</Banner>}
              <form onSubmit={continueEmail} noValidate={false}>
                <label className="field">{t('auth.emailAddress')}
                  <input className="field-input" type="email" autoComplete="username" required placeholder={t('auth.emailPlaceholder')} value={email} onChange={e => setEmail(e.target.value)} />
                </label>
                <Button variant="primary" type="submit" className="block" disabled={!configured}>{t('auth.continueEmail')}</Button>
              </form>
            </>
          ) : (
            <form onSubmit={signIn}>
              <div className="signin-email-chip">
                <span>{email}</span>
                <Button variant="link" onClick={() => { setStep('email'); setPassword(''); setProblem(null); }}>{t('auth.change')}</Button>
              </div>
              {message && <Banner tone={problem === 'offline' ? 'attention' : 'danger'}>{message}</Banner>}
              <label className="field" style={{ marginTop: 22 }}>{t('auth.password')}
                <input ref={passwordRef} className="field-input" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />
              </label>
              <Button variant="primary" type="submit" className="block" disabled={busy || !configured}>{busy ? t('auth.working') : t('auth.submit')}</Button>
              <p className="muted" style={{ marginTop: 16, fontSize: 15 }}>
                {t('auth.newAccountPrompt')} <Button variant="link" disabled={busy} onClick={() => void signUp()}>{t('auth.createAccount')}</Button>
              </p>
            </form>
          )}
        </section>
      </main>
      <footer className="public-foot">
        <LegalLinks />
        <PrefsCluster prefs={prefs} onPrefs={onPrefs} />
      </footer>
    </div>
  );
}
